"use client"

import { useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Copy, Check, Trash2 } from "lucide-react"
import { toast } from "sonner"

type Separator = "-" | "_" | "."

interface SlugOptions {
  separator: Separator
  lowercase: boolean
  removeStopWords: boolean
  maxLength: number
  transliterate: boolean
}

const stopWords = new Set([
  "a", "an", "the", "and", "or", "but", "in", "on", "at", "to", "for",
  "of", "with", "by", "from", "as", "is", "was", "are", "were", "been",
  "be", "have", "has", "had", "do", "does", "did", "will", "would",
  "could", "should", "may", "might", "must", "shall", "can", "need",
  "this", "that", "these", "those", "it", "its"
])

// Basic transliteration map
const transliterationMap: Record<string, string> = {
  "à": "a", "á": "a", "â": "a", "ã": "a", "ä": "a", "å": "a", "æ": "ae",
  "ç": "c", "è": "e", "é": "e", "ê": "e", "ë": "e",
  "ì": "i", "í": "i", "î": "i", "ï": "i",
  "ñ": "n", "ò": "o", "ó": "o", "ô": "o", "õ": "o", "ö": "o", "ø": "o",
  "ù": "u", "ú": "u", "û": "u", "ü": "u", "ý": "y", "ÿ": "y",
  "ß": "ss", "œ": "oe", "ž": "z", "š": "s", "đ": "d",
  "ą": "a", "ć": "c", "ę": "e", "ł": "l", "ń": "n", "ś": "s", "ź": "z", "ż": "z",
  "а": "a", "б": "b", "в": "v", "г": "g", "д": "d", "е": "e", "ё": "yo",
  "ж": "zh", "з": "z", "и": "i", "й": "y", "к": "k", "л": "l", "м": "m",
  "н": "n", "о": "o", "п": "p", "р": "r", "с": "s", "т": "t", "у": "u",
  "ф": "f", "х": "kh", "ц": "ts", "ч": "ch", "ш": "sh", "щ": "shch",
  "ы": "y", "э": "e", "ю": "yu", "я": "ya", "ь": "", "ъ": "",
  "і": "i", "ї": "yi", "є": "ye", "ґ": "g"
}

function transliterate(str: string): string {
  return str
    .split("")
    .map((char) => {
      const lower = char.toLowerCase()
      if (transliterationMap[lower]) {
        return char === lower
          ? transliterationMap[lower]
          : transliterationMap[lower].toUpperCase()
      }
      return char
    })
    .join("")
}

function slugify(text: string, options: SlugOptions): string {
  let result = text

  // Transliterate if enabled
  if (options.transliterate) {
    result = transliterate(result)
  }

  // Convert to lowercase if enabled
  if (options.lowercase) {
    result = result.toLowerCase()
  }

  // Remove stop words if enabled
  if (options.removeStopWords) {
    const words = result.split(/\s+/)
    result = words
      .filter((word) => !stopWords.has(word.toLowerCase()))
      .join(" ")
  }

  // Replace special characters and spaces
  result = result
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^\w\s-]/g, "") // Remove non-word chars except spaces and hyphens
    .replace(/\s+/g, options.separator) // Replace spaces with separator
    .replace(new RegExp(`${options.separator}+`, "g"), options.separator) // Remove duplicate separators
    .replace(new RegExp(`^${options.separator}|${options.separator}$`, "g"), "") // Trim separators

  // Apply max length
  if (options.maxLength > 0 && result.length > options.maxLength) {
    result = result.slice(0, options.maxLength)
    // Don't end with separator
    result = result.replace(new RegExp(`${options.separator}$`), "")
  }

  return result
}

export function SlugifyTool() {
  const [input, setInput] = useState("")
  const [options, setOptions] = useState<SlugOptions>({
    separator: "-",
    lowercase: true,
    removeStopWords: false,
    maxLength: 0,
    transliterate: true,
  })
  const [copied, setCopied] = useState(false)

  const output = useMemo(() => (input ? slugify(input, options) : ""), [input, options])

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Copied to clipboard")
  }

  const clear = () => {
    setInput("")
  }

  const loadSample = () => {
    setInput("How to Create SEO-Friendly URLs: A Complete Guide (2024)")
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Slugify Tool</CardTitle>
          <CardDescription>
            Convert text to URL-friendly slugs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Separator</Label>
              <Select
                value={options.separator}
                onValueChange={(v) => setOptions({ ...options, separator: v as Separator })}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-">Hyphen (-)</SelectItem>
                  <SelectItem value="_">Underscore (_)</SelectItem>
                  <SelectItem value=".">Dot (.)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Max Length</Label>
              <Input
                type="number"
                placeholder="0 = unlimited"
                value={options.maxLength || ""}
                onChange={(e) =>
                  setOptions({ ...options, maxLength: parseInt(e.target.value) || 0 })
                }
                className="w-[120px]"
              />
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="lowercase"
                checked={options.lowercase}
                onCheckedChange={(v) => setOptions({ ...options, lowercase: v })}
              />
              <Label htmlFor="lowercase">Lowercase</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="transliterate"
                checked={options.transliterate}
                onCheckedChange={(v) => setOptions({ ...options, transliterate: v })}
              />
              <Label htmlFor="transliterate">Transliterate</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="stopwords"
                checked={options.removeStopWords}
                onCheckedChange={(v) => setOptions({ ...options, removeStopWords: v })}
              />
              <Label htmlFor="stopwords">Remove stop words</Label>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="input">Original Text</Label>
            <Textarea
              id="input"
              placeholder="Enter text to convert to slug..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          {/* Output */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Generated Slug</Label>
              {output && (
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              )}
            </div>
            <div className="p-3 rounded-lg bg-muted font-mono text-sm min-h-[44px] break-all">
              {output || <span className="text-muted-foreground">Slug will appear here...</span>}
            </div>
            {output && (
              <p className="text-xs text-muted-foreground">
                Length: {output.length} characters
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Slug Best Practices</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <ul className="list-disc list-inside space-y-1">
            <li>Keep slugs short and descriptive (50-60 characters max)</li>
            <li>Use hyphens to separate words (SEO-friendly)</li>
            <li>Avoid stop words (a, the, and, etc.) for cleaner URLs</li>
            <li>Use lowercase for consistency</li>
            <li>Include relevant keywords for SEO</li>
            <li>Avoid special characters and numbers when possible</li>
          </ul>
          <div className="mt-4 p-3 rounded bg-muted/50">
            <p className="font-medium mb-1">Examples:</p>
            <code className="text-xs">
              &quot;How to Build a Website&quot; → how-to-build-a-website<br />
              &quot;10 Tips & Tricks for 2024!&quot; → 10-tips-tricks-for-2024<br />
              &quot;Café résumé naïve&quot; → cafe-resume-naive
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
